import React, { useEffect, useState } from 'react';
import { CURRENCY } from '../config';
import { getCachedSchoolName } from '../utils/branding';
import { buildStatement } from '../utils/challan';
import api from '../api/axios';

export default function FeeChallan({
  fee,
  child,
  student,
  allFees,
  structures,
  students,
  onClose,
}) {
  const [schoolName, setSchoolName] = useState(getCachedSchoolName());

  useEffect(() => {
    api.get('/public/branding')
      .then(r => { if (r.data?.school_name) setSchoolName(r.data.school_name); })
      .catch(() => {});
  }, []);

  if (!fee || !child) return null;

  const resolvedStudent = student
    || (students || []).find(s => s.id === fee.student_id)
    || null;

  const stmt = buildStatement(allFees || [], fee, structures || [], resolvedStudent);
  const isPaid = stmt.isPaid;

  const challanNo = `CH-${String(fee.id).padStart(6, '0')}`;
  const docTitle = isPaid ? 'Payment Receipt' : 'Fee Statement';
  const monthLabel = new Date(fee.due_date).toLocaleDateString('en', {
    month: 'long', year: 'numeric',
  });

  const handlePrint = () => window.print();
  const fmt = (v) => `${CURRENCY}${Number(v || 0).toFixed(2)}`;
  const grandTotal = stmt.grandTotal;

  const monthsWithItems = stmt.months.filter(m => m.items.length > 0);

  const feeBase   = fee.base_amount ?? fee.amount ?? 0;
  const feeNet    = fee.amount || 0;
  const feeDiscPct = fee.discount_percentage || 0;
  const feeIsWaived = feeNet <= 0.01 && feeBase > 0.01;

  return (
    <>
      <style>{`
        @media print {
          @page { size: A4 landscape; margin: 10mm; }
          html, body {
            width: 297mm !important;
            height: auto !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: visible !important;
            background: white !important;
          }
          body * { visibility: hidden !important; }
          #fee-challan, #fee-challan * { visibility: visible !important; }
          #fee-challan {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 277mm !important;
            max-width: 277mm !important;
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            box-shadow: none !important;
            border-radius: 0 !important;
            font-size: 9pt;
          }
          .challan-print-shell,
          .challan-print-shell * {
            max-height: none !important;
            max-width: none !important;
            overflow: visible !important;
            box-shadow: none !important;
            border-radius: 0 !important;
          }
          .no-print, .no-print * { display: none !important; }
          .print\\:grid-cols-3 {
            display: grid !important;
            grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
            gap: 6mm !important;
          }
          .break-inside-avoid {
            break-inside: avoid !important;
            page-break-inside: avoid !important;
          }
          #fee-challan .copy-row {
            break-inside: avoid !important;
            page-break-inside: avoid !important;
          }
        }
      `}</style>

      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4 challan-print-shell"
        onClick={onClose}
      >
        <div
          className="bg-white rounded shadow-xl w-full max-w-5xl flex flex-col max-h-[95vh] challan-print-shell"
          onClick={e => e.stopPropagation()}
        >
          {/* TOOLBAR */}
          <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50 no-print flex-shrink-0">
            <div className="flex items-center gap-2">
              <i className={`fas ${isPaid ? 'fa-receipt' : 'fa-file-invoice'} text-blue-600`}></i>
              <h2 className="font-semibold text-gray-800 text-base">{docTitle}</h2>
              <span className="text-sm font-mono text-gray-500">{challanNo}</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handlePrint}
                className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
              >
                <i className="fas fa-print mr-1"></i> Print / Save PDF
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded text-sm hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>

          {/* SCROLLABLE BODY */}
          <div className="overflow-y-auto flex-1 challan-print-shell">
            <div id="fee-challan" className="print-area p-6 sm:p-8 bg-white text-gray-900">

              {/* HEADER */}
              <div className="flex items-start justify-between border-b-2 border-gray-800 pb-4 mb-6">
                <div>
                  <h1 className="text-2xl font-bold tracking-tight">{schoolName}</h1>
                  <p className="text-sm text-gray-600 mt-1">{child.campus_name}</p>
                </div>
                <div className="text-right">
                  <p className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">
                    {docTitle}
                  </p>
                  <p className="text-base font-bold mt-1">{monthLabel}</p>
                  <p className="text-xs font-mono text-gray-600 mt-1">{challanNo}</p>
                </div>
              </div>

              {/* STUDENT META */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3 text-[12px] mb-6">
                <div>
                  <p className="text-gray-500 uppercase tracking-wide text-[10px] font-semibold">Student</p>
                  <p className="font-semibold text-sm mt-0.5">{child.name}</p>
                </div>
                <div>
                  <p className="text-gray-500 uppercase tracking-wide text-[10px] font-semibold">Reg #</p>
                  <p className="font-mono text-sm mt-0.5">{child.registration_number || '—'}</p>
                </div>
                <div>
                  <p className="text-gray-500 uppercase tracking-wide text-[10px] font-semibold">Class</p>
                  <p className="font-semibold text-sm mt-0.5">{child.class_name}</p>
                </div>
                <div>
                  <p className="text-gray-500 uppercase tracking-wide text-[10px] font-semibold">Due Date</p>
                  <p className={`font-semibold text-sm mt-0.5 ${isPaid ? '' : 'text-red-700'}`}>
                    {fee.due_date || '—'}
                  </p>
                </div>
              </div>

              {/* 3 COPIES */}
              <div className="copy-row mt-6 grid grid-cols-1 lg:grid-cols-3 gap-3 print:grid-cols-3 print:gap-2">
                {['Bank Copy', 'School Copy', 'Student Copy'].map(copyLabel => (
                  <div
                    key={copyLabel}
                    className="relative overflow-hidden border border-gray-400 p-3 text-[11px] leading-tight break-inside-avoid"
                  >
                    {isPaid && !feeIsWaived && (
                      <div className="paid-watermark pointer-events-none select-none">
                        <span>PAID</span>
                      </div>
                    )}
                    {feeIsWaived && isPaid && (
                      <div className="paid-watermark pointer-events-none select-none">
                        <span style={{ color: '#9333ea' }}>WAIVED</span>
                      </div>
                    )}
                    <div className="relative z-10">
                      <div className="flex justify-between items-center border-b border-gray-300 pb-1.5 mb-2">
                        <span className="font-bold text-[10px] uppercase tracking-widest">
                          {copyLabel}
                        </span>
                        <span className="font-mono text-[9px] text-gray-500">{challanNo}</span>
                      </div>

                      <p className="font-semibold text-[11px] truncate">{child.name}</p>
                      <p className="text-gray-600 text-[10px] truncate">
                        {child.class_name} · {child.registration_number || '—'}
                      </p>

                      <table className="w-full border-collapse text-[9px] my-2">
                        <tbody>
                          {isPaid ? (
                            <>
                              <tr>
                                <td className="border border-gray-300 px-1.5 py-1 font-semibold">
                                  {fee.fee_type}
                                </td>
                                <td className="border border-gray-300 px-1.5 py-1 text-right">
                                  {fmt(feeBase)}
                                </td>
                              </tr>
                              {feeDiscPct > 0 && (
                                <>
                                  <tr className="bg-green-50">
                                    <td className="border border-gray-300 px-1.5 py-1 text-green-800 pl-3 text-[8px]">
                                      Discount ({feeDiscPct}%)
                                    </td>
                                    <td className="border border-gray-300 px-1.5 py-1 text-right text-green-800 text-[8px] font-semibold">
                                      −{fmt(feeBase - feeNet)}
                                    </td>
                                  </tr>
                                  <tr className={feeIsWaived ? 'bg-purple-50' : 'bg-gray-50'}>
                                    <td className={`border border-gray-300 px-1.5 py-1 font-bold text-[9px] uppercase tracking-wide ${
                                      feeIsWaived ? 'text-purple-800' : ''
                                    }`}>
                                      {feeIsWaived ? 'Waived' : 'Paid'}
                                    </td>
                                    <td className={`border border-gray-300 px-1.5 py-1 text-right font-bold ${
                                      feeIsWaived ? 'text-purple-800' : ''
                                    }`}>
                                      {fmt(feeNet)}
                                    </td>
                                  </tr>
                                </>
                              )}
                              {feeDiscPct === 0 && (
                                <tr className="bg-green-50">
                                  <td className="border border-gray-300 px-1.5 py-1 font-bold text-[9px] uppercase tracking-wide">
                                    Paid
                                  </td>
                                  <td className="border border-gray-300 px-1.5 py-1 text-right font-bold">
                                    {fmt(fee.paid_amount)}
                                  </td>
                                </tr>
                              )}
                            </>
                          ) : (
                            <>
                              {monthsWithItems.map((m) => (
                                <React.Fragment key={m.monthKey}>
                                  <tr>
                                    <td
                                      colSpan={2}
                                      className="border border-gray-300 px-1.5 py-1 bg-gray-100 font-bold uppercase tracking-wide text-[8px] text-gray-700"
                                    >
                                      {m.monthLabel}
                                    </td>
                                  </tr>
                                  {m.items.map((it, i) => {
                                    const hasDisc = (it.discountPct || 0) > 0;
                                    if (hasDisc) {
                                      return (
                                        <React.Fragment key={i}>
                                          <tr>
                                            <td className="border border-gray-300 px-1.5 py-1">
                                              {it.kind === 'late' ? 'Late Fee' : it.label}
                                            </td>
                                            <td className="border border-gray-300 px-1.5 py-1 text-right">
                                              {fmt(it.base)}
                                            </td>
                                          </tr>
                                          <tr className="bg-green-50">
                                            <td className="border border-gray-300 px-1.5 py-1 text-green-800 pl-3 text-[8px]">
                                              Disc ({it.discountPct}%)
                                            </td>
                                            <td className="border border-gray-300 px-1.5 py-1 text-right text-green-800 text-[8px] font-semibold">
                                              −{fmt(it.base - it.net)}
                                            </td>
                                          </tr>
                                          <tr className={it.isWaived ? 'bg-purple-50' : 'bg-gray-50'}>
                                            <td className={`border border-gray-300 px-1.5 py-1 pl-3 text-[8px] font-semibold ${
                                              it.isWaived ? 'text-purple-800' : ''
                                            }`}>
                                              {it.isWaived ? 'Waived' : 'Net'}
                                            </td>
                                            <td className={`border border-gray-300 px-1.5 py-1 text-right text-[8px] font-semibold ${
                                              it.isWaived ? 'text-purple-800' : ''
                                            }`}>
                                              {fmt(it.isWaived ? 0 : it.balance)}
                                            </td>
                                          </tr>
                                        </React.Fragment>
                                      );
                                    }
                                    return (
                                      <tr key={i} className={it.kind === 'late' ? 'bg-amber-50' : ''}>
                                        <td className={`border border-gray-300 px-1.5 py-1 ${
                                          it.kind === 'late' ? 'text-amber-800 pl-3' : ''
                                        }`}>
                                          {it.kind === 'late' ? 'Late Fee' : it.label}
                                        </td>
                                        <td className={`border border-gray-300 px-1.5 py-1 text-right ${
                                          it.kind === 'late' ? 'text-amber-800 font-semibold' : ''
                                        }`}>
                                          {fmt(it.balance)}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </React.Fragment>
                              ))}
                              <tr className="bg-gray-100">
                                <td className="border border-gray-300 px-1.5 py-1 font-bold text-[9px] uppercase tracking-wide">
                                  Grand Total
                                </td>
                                <td className="border border-gray-300 px-1.5 py-1 text-right font-bold">
                                  {fmt(grandTotal)}
                                </td>
                              </tr>
                            </>
                          )}
                        </tbody>
                      </table>

                      <p className="text-[9px] text-gray-500">
                        Due: <strong>{fee.due_date}</strong>
                      </p>

                      <div className="pt-4 mt-2 border-t border-dashed border-gray-300">
                        <div className="border-t border-gray-400 w-20"></div>
                        <p className="text-[8px] text-gray-500 mt-1">
                          {isPaid ? 'Received By' : 'Bank / Cashier'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* FOOTER */}
              <div className="mt-5 pt-3 border-t border-gray-300 text-[10px] text-gray-600 space-y-1">
                {isPaid ? (
                  <>
                    <p><strong>Note:</strong> This is a computer-generated payment receipt. No signature is required.</p>
                    <p>Retain this document as proof of payment.</p>
                  </>
                ) : (
                  <>
                    <p><strong>Note:</strong> This is a cumulative fee statement. One payment clears every month listed above.</p>
                    <p>Pay at the school office or at any designated bank branch. Partial payments are not accepted.</p>
                    <p>Late fees are computed from the current class fee policy.</p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}